export type KlacksLineKind = 'show_off' | 'fact';

export interface KlacksLine {
  id: string;
  kind: KlacksLineKind;
  i18nKey: string;
  defaultValue: string;
  /** Decorative companion, never localized. */
  ascii: string;
  /** "Almost done" lines: held back early in a reply so they don't lie. */
  completionHint?: true;
}

export interface KlacksDeck {
  next(elapsedMs: number): KlacksLine;
}

const pad = (n: number) => String(n).padStart(2, '0');

const showOff = (n: number, ascii: string, defaultValue: string, completionHint?: true): KlacksLine => ({
  id: `S${pad(n)}`,
  kind: 'show_off',
  i18nKey: `WORKING_KLACKS_S${pad(n)}`,
  defaultValue,
  ascii,
  ...(completionHint && { completionHint }),
});

const fact = (n: number, ascii: string, defaultValue: string): KlacksLine => ({
  id: `F${pad(n)}`,
  kind: 'fact',
  i18nKey: `WORKING_KLACKS_F${pad(n)}`,
  defaultValue,
  ascii,
});

export const KLACKS_LINES: readonly KlacksLine[] = [
  showOff(1, '(⌐■_■)', 'A Klacks. Almost done.', true),
  showOff(2, 'ᕦ(ò_óˇ)ᕤ', 'Barely breaking a sweat.'),
  showOff(3, 'c[_]', 'Hold my coffee …'),
  showOff(4, '(ノ°▽°)ノ', 'Juggling three tools. Still a Klacks.'),
  showOff(5, 'ʕ•ᴥ•ʔ', 'I do this before breakfast.'),
  showOff(6, '¯\\_(ツ)_/¯', 'Hard? Never heard of it.'),
  showOff(7, '( •_•)>⌐■-■', 'Too easy. Almost boring.'),
  showOff(8, 'ε=ε=┌( >_<)┘', 'Not even warmed up yet.'),
  showOff(9, '(☞ﾟヮﾟ)☞', 'Running like clockwork.'),
  showOff(10, '● klack!', 'Klack, klack … almost done.', true),
  showOff(11, '(￣▽￣)', 'All under control.'),
  showOff(12, '( ˘▽˘)っ♨', 'Piece of cake. Literally a Klacks.'),
  showOff(13, 'ᕙ(⇀‸↼‶)ᕗ', 'Lifting the heavy stuff. Feels light.'),
  showOff(14, '(ง •̀_•́)ง', "One more step, then it's done.", true),
  showOff(15, '♪(´ε｀ )', 'Humming while I work.'),
  showOff(16, '(•̀ᴗ•́)و', 'Easy does it.'),
  showOff(17, '[■■■□□]', 'Loading brilliance …'),
  showOff(18, '(っ˘ڡ˘ς)', 'Tastes like a Klacks of jam.'),
  showOff(19, 'ヽ(・∀・)ﾉ', 'My favourite kind of task.'),
  showOff(20, '(─‿‿─)', 'Could do this with my eyes closed.'),
  fact(1, '● klack!', '"Klack!" is the sound of something soft landing. That\'s where "Klacks" comes from.'),
  fact(2, '(っ˘ڡ˘ς)', 'A Klacks was first a dollop: a Klacks of jam, butter or mustard.'),
  fact(3, '(⌐■_■)', '"Das ist ein Klacks" means: no effort at all. That\'s why we\'re called Klax.'),
  fact(4, 'ʕ•ᴥ•ʔ', '"Klacks" dates back to the 18th century. Easy never goes out of style.'),
  fact(5, '( •_•)✎', '"Klacks" is related to "Klecks", a blob of ink. Don\'t worry, I won\'t smudge.'),
  fact(6, '(☞ﾟヮﾟ)☞', '"Klacks" is everyday German for "a piece of cake".'),
];

export const KLACKS_DWELL_MS = { show_off: 3_000, fact: 6_000 } as const;
export const KLACKS_COMPLETION_HINT_MIN_ELAPSED_MS = 15_000;

const FACT_EVERY_NTH_SLOT = 3;

function shuffle<T>(items: readonly T[], rng: () => number): T[] {
  const result = [...items];
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
}

/** All 26 lines in a fresh order, with a fact in every 3rd slot while facts remain. */
function buildRound(rng: () => number): KlacksLine[] {
  const showOffs = shuffle(KLACKS_LINES.filter((line) => line.kind === 'show_off'), rng);
  const facts = shuffle(KLACKS_LINES.filter((line) => line.kind === 'fact'), rng);
  const round: KlacksLine[] = [];
  while (showOffs.length || facts.length) {
    const isFactSlot = (round.length + 1) % FACT_EVERY_NTH_SLOT === 0;
    const useFacts = facts.length > 0 && (isFactSlot || showOffs.length === 0);
    round.push((useFacts ? facts : showOffs).shift() as KlacksLine);
  }
  return round;
}

export function createKlacksDeck(rng: () => number = Math.random): KlacksDeck {
  let queue = buildRound(rng);
  let lastShown: KlacksLine | undefined;

  return {
    next(elapsedMs) {
      if (queue.length === 0) {
        queue = buildRound(rng);
        // A new round must not open with the line that just closed the last one.
        if (queue[0].id === lastShown?.id) queue.push(queue.shift() as KlacksLine);
      }
      const isTooEarlyForHints = elapsedMs < KLACKS_COMPLETION_HINT_MIN_ELAPSED_MS;
      const index = Math.max(
        queue.findIndex((line) => !(isTooEarlyForHints && line.completionHint)),
        0
      );
      [lastShown] = queue.splice(index, 1);
      return lastShown;
    },
  };
}

const MAX_DECKS = 20;
const decks = new Map<string, { deck: KlacksDeck; startedAt: number }>();

/** One deck per reply, so a remount of the working row continues where it left off. */
export function getGenerationDeck(generationKey: string) {
  const existing = decks.get(generationKey);
  if (existing) return existing;
  if (decks.size >= MAX_DECKS) decks.delete(decks.keys().next().value as string);
  const created = { deck: createKlacksDeck(), startedAt: Date.now() };
  decks.set(generationKey, created);
  return created;
}

export function deleteGenerationDeck(generationKey: string) {
  decks.delete(generationKey);
}
